import { useEffect, useState } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { subscriptionApi, type PlanDetails, type SubscriptionResponse } from '../../api/subscriptionApi';
import { useTranslation } from '../../i18n';
import { formatCalendarDate } from '../../utils/dateTranslations';
import { openStarsCheckout } from '../../utils/starsCheckout';
import { useAuth } from '../../contexts/AuthContext';
import { entitlementsOf } from '../../utils/entitlements';
import type { FeatureEntitlements } from '../../types/api';
import { Button, Card, ErrorMessage, Loading, StatusBadge } from '../ui';
import { captureEvent } from '../../posthog';

const PLAN_ORDER = ['FREE', 'TRIAL', 'SOLO', 'PRO'] as const;

const SOLO_UNLOCKS = ['workoutMode', 'workoutHistory', 'createOwn'] as const;
const PRO_UNLOCKS = [
  'workoutMode',
  'workoutHistory',
  'unlimitedClients',
  'autoProlong',
  'proView',
  'clientHistory',
] as const;

function hasUnlock(
  key: string,
  entitlements: FeatureEntitlements,
  currentPlanCode: string,
): boolean {
  switch (key) {
    case 'workoutMode':
      return entitlements.workoutMode;
    case 'workoutHistory':
      return entitlements.workoutHistory;
    case 'createOwn':
      return entitlements.createOwnBlocksAndPrograms;
    case 'unlimitedClients':
      return currentPlanCode === 'PRO';
    case 'autoProlong':
      return entitlements.autoProlongation;
    case 'proView':
      return entitlements.proView;
    case 'clientHistory':
      return entitlements.clientWorkoutHistory;
    default:
      return false;
  }
}

function unlockLabels(
  planCode: 'SOLO' | 'PRO',
  entitlements: FeatureEntitlements,
  currentPlanCode: string,
  translate: (key: string) => string,
): string[] {
  const keys = planCode === 'SOLO' ? SOLO_UNLOCKS : PRO_UNLOCKS;
  return keys
    .filter((key) => !hasUnlock(key, entitlements, currentPlanCode))
    .map((key) => translate(`subscription.access.${key}`));
}

function FeatureList({
  items,
  theme,
  className = 'mb-4',
}: {
  items: string[];
  theme: ReturnType<typeof useTheme>;
  className?: string;
}) {
  if (items.length === 0) return null;
  return (
    <ul className={`space-y-2 ${className}`.trim()}>
      {items.map((feature) => (
        <li key={feature} className="flex items-start gap-2 text-sm" style={{ color: theme.text.secondary }}>
          <span className="mt-0.5 shrink-0" style={{ color: theme.brand.solid }}>
            ✓
          </span>
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  );
}

function planRank(code: string): number {
  const idx = PLAN_ORDER.indexOf(code as (typeof PLAN_ORDER)[number]);
  return idx >= 0 ? idx : -1;
}

function formatPrice(priceMonthly: number, currency: string): string {
  if (currency === 'XTR') return `${priceMonthly} ⭐`;
  const symbols: Record<string, string> = { USD: '$', EUR: '€', UAH: '₴', GBP: '£' };
  return `${symbols[currency] || currency}${priceMonthly.toFixed(2)}`;
}

function daysLeft(periodEnd: string | null | undefined): number | null {
  if (!periodEnd) return null;
  const end = new Date(periodEnd).getTime();
  if (!Number.isFinite(end)) return null;
  const msLeft = end - Date.now();
  if (msLeft <= 0) return 0;
  return Math.max(1, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
}

function AccessRow({
  label,
  included,
  hint,
  theme,
}: {
  label: string;
  included: boolean;
  hint?: string;
  theme: ReturnType<typeof useTheme>;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm" style={{ color: theme.text.primary }}>
          {label}
        </p>
        {hint && (
          <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
            {hint}
          </p>
        )}
      </div>
      <span
        className="shrink-0 text-xs font-semibold"
        style={{ color: included ? 'rgba(22,163,74,1)' : theme.text.muted }}
      >
        {included ? '✓' : '—'}
      </span>
    </div>
  );
}

export default function Subscription() {
  const { translate, language } = useTranslation();
  const theme = useTheme();
  const { user, refreshUser } = useAuth();
  const entitlements = entitlementsOf(user);

  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  const [trialBusy, setTrialBusy] = useState(false);

  useEffect(() => {
    void loadSubscription();
    // Load once on mount; later refreshes are triggered by user actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSubscription = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await subscriptionApi.getStatus();
      setSubscription(data);
      await refreshUser();
    } catch (err) {
      setError(translate('subscription.loadError'));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = async (planCode: string) => {
    try {
      setCheckoutError(null);
      setBusyPlan(planCode);
      captureEvent('subscription_checkout_started', { plan_code: planCode });
      const status = await openStarsCheckout(planCode);
      captureEvent('subscription_checkout_result', { plan_code: planCode, status });
      if (status === 'paid') {
        await loadSubscription();
        await refreshUser();
      } else if (status === 'failed') {
        setCheckoutError(translate('subscription.checkoutFailed'));
      }
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      captureEvent('subscription_checkout_result', { plan_code: planCode, status: 'error' });
      setCheckoutError(translate('subscription.checkoutFailed'));
    } finally {
      setBusyPlan(null);
    }
  };

  const handleCancel = async () => {
    if (!confirm(translate('subscription.confirmCancel'))) return;
    try {
      await subscriptionApi.cancel();
      captureEvent('subscription_cancelled');
      await loadSubscription();
      await refreshUser();
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
    }
  };

  const handleStartTrial = async () => {
    try {
      setCheckoutError(null);
      setTrialBusy(true);
      await subscriptionApi.activateTrial();
      captureEvent('trial_activated', { source: 'subscription_page' });
      await loadSubscription();
      await refreshUser();
    } catch (err) {
      console.error('Failed to activate trial:', err);
      setCheckoutError(translate('subscription.trial.activateFailed'));
    } finally {
      setTrialBusy(false);
    }
  };

  if (isLoading) {
    return <Loading message={translate('common.loading')} />;
  }

  if (error || !subscription) {
    return (
      <ErrorMessage
        message={error || translate('subscription.loadError')}
        onRetry={loadSubscription}
        retryLabel={translate('subscription.retry')}
      />
    );
  }

  const { plan, status, currentPeriodEnd, cancelAtPeriodEnd, availablePlans } = subscription;
  const currentRank = planRank(plan.code);
  const isPaid = plan.code === 'SOLO' || plan.code === 'PRO';
  const isTrial = plan.code === 'TRIAL' || status === 'TRIALING';
  const isCoach = Boolean(user?.hasTrainees);
  const isCoachedTrainee = entitlements.hasActiveCoach || Boolean(user?.hasCoaches);
  const trialLeft = daysLeft(currentPeriodEnd);
  const showTrialOffer = plan.code === 'FREE' && !subscription.trialUsed;
  const canActivateTrial = subscription.trialAvailable && !isCoachedTrainee;
  const trialFeatures = [
    translate('subscription.access.workoutMode'),
    translate('subscription.access.workoutHistory'),
    translate('subscription.access.proView'),
    translate('subscription.access.clientHistory'),
    translate('subscription.access.autoProlong'),
  ];

  const paidPlans: PlanDetails[] = (availablePlans || [])
    .filter((p) => p.code !== 'FREE' && p.priceMonthly > 0)
    .sort((a, b) => planRank(a.code) - planRank(b.code));

  const availableCodes = new Set((availablePlans || []).map((p) => p.code));
  const soloPurchasable = availableCodes.has('SOLO');
  const proPurchasable = availableCodes.has('PRO');
  const showSoloRestriction = plan.code !== 'SOLO' && !soloPurchasable;
  const showProRestriction = plan.code !== 'PRO' && !proPurchasable;

  const periodLabel = currentPeriodEnd
    ? isTrial
      ? `${translate('subscription.expiresOn')} ${formatCalendarDate(currentPeriodEnd, language)}`
      : cancelAtPeriodEnd
        ? `${translate('subscription.expiresOn')} ${formatCalendarDate(currentPeriodEnd, language)}`
        : `${translate('subscription.renewsOn')} ${formatCalendarDate(currentPeriodEnd, language)}`
    : null;

  const statusVariant = status === 'ACTIVE' ? 'success' : status === 'PAST_DUE' ? 'error' : 'warning';
  const cascadeHint = isCoachedTrainee ? translate('subscription.access.fromCoach') : undefined;
  const currentFeatures: { label: string; hint?: string }[] = [];
  if (entitlements.workoutMode) {
    currentFeatures.push({
      label: translate('subscription.access.workoutMode'),
      hint: cascadeHint,
    });
  }
  if (entitlements.workoutHistory) {
    currentFeatures.push({
      label: translate('subscription.access.workoutHistory'),
      hint: cascadeHint,
    });
  }
  if (entitlements.createOwnBlocksAndPrograms) {
    currentFeatures.push({ label: translate('subscription.access.createOwn') });
  }
  if (plan.code === 'PRO') {
    currentFeatures.push({ label: translate('subscription.access.unlimitedClients') });
  } else if (entitlements.canAddTrainee) {
    currentFeatures.push({ label: translate('subscription.access.addTrainee') });
  }
  if (entitlements.proView) {
    currentFeatures.push({ label: translate('subscription.access.proView') });
  }
  if (entitlements.clientWorkoutHistory) {
    currentFeatures.push({ label: translate('subscription.access.clientHistory') });
  }
  if (entitlements.autoProlongation) {
    currentFeatures.push({ label: translate('subscription.access.autoProlong') });
  }

  const upgradePlans = paidPlans.filter((p) => planRank(p.code) > currentRank);
  const soloUnlocks = unlockLabels('SOLO', entitlements, plan.code, translate);
  const proUnlocks = unlockLabels('PRO', entitlements, plan.code, translate);

  const contextNote = isCoachedTrainee
    ? translate('subscription.eligibility.coachedAccess')
    : isCoach
      ? translate('subscription.eligibility.coachAccess')
      : translate('subscription.eligibility.soloAccess');

  return (
    <div className="px-5 pb-10 pt-2 max-w-lg mx-auto">
      <header className="mb-6">
        <h1
          className="text-2xl font-extrabold tracking-tight"
          style={{ color: theme.text.primary, letterSpacing: '-0.4px' }}
        >
          {translate('subscription.mySubscription')}
        </h1>
        <p className="text-sm mt-1" style={{ color: theme.text.muted }}>
          {translate('subscription.subtitle')}
        </p>
      </header>

      {checkoutError && (
        <div
          className="mb-4 px-4 py-3 rounded-2xl text-sm"
          style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: 'rgba(220, 38, 38, 1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        >
          {checkoutError}
        </div>
      )}

      <Card variant="overlap" noHover className="mb-6 !rounded-3xl !p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p
              className="text-xs font-bold uppercase tracking-widest mb-2"
              style={{ color: theme.text.muted }}
            >
              {translate('subscription.currentPlan')}
            </p>
            <h2 className="text-2xl font-extrabold" style={{ color: theme.text.primary }}>
              {translate(`subscription.plans.${plan.code}.name`)}
            </h2>
            <p className="text-sm mt-1 leading-snug" style={{ color: theme.text.secondary }}>
              {translate(`subscription.plans.${plan.code}.description`)}
            </p>
          </div>
          <StatusBadge
            status={translate(`subscription.status.${status}`)}
            variant={statusVariant}
            size="sm"
          />
        </div>

        {(isPaid || isTrial) && (
          <div className="flex items-end justify-between gap-3">
            <div>
              {isPaid ? (
                <p className="text-xl font-bold tabular-nums" style={{ color: theme.text.primary }}>
                  {formatPrice(plan.priceMonthly, plan.currency)}
                  <span className="text-sm font-normal ml-1" style={{ color: theme.text.muted }}>
                    {translate('subscription.price.perMonth')}
                  </span>
                </p>
              ) : (
                <p className="text-xl font-bold" style={{ color: theme.text.primary }}>
                  {translate('subscription.trial.badge')}
                </p>
              )}
              {periodLabel && (
                <p className="text-xs mt-1" style={{ color: theme.text.muted }}>
                  {periodLabel}
                </p>
              )}
              {isTrial && trialLeft !== null && (
                <p className="text-xs mt-1" style={{ color: theme.text.muted }}>
                  {translate('subscription.trial.daysLeft').replace('{days}', String(trialLeft))}
                </p>
              )}
            </div>
            {plan.code === 'PRO' && (
              <span className="text-2xl" aria-hidden>
                👑
              </span>
            )}
            {plan.code === 'TRIAL' && (
              <span className="text-2xl" aria-hidden>
                🎁
              </span>
            )}
            {plan.code === 'SOLO' && (
              <span className="text-2xl" aria-hidden>
                🏃
              </span>
            )}
          </div>
        )}

        {isPaid && (
          <p className="text-sm mt-4 leading-snug" style={{ color: theme.text.secondary }}>
            {contextNote}
          </p>
        )}
      </Card>

      {showTrialOffer && (
        <Card noHover className="mb-6 !rounded-3xl !p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="text-lg font-bold mb-1" style={{ color: theme.text.primary }}>
                {translate('subscription.trial.title')}
              </h3>
              <p className="text-sm leading-snug" style={{ color: theme.text.secondary }}>
                {isCoachedTrainee
                  ? translate('subscription.trial.coachedBody')
                  : translate('subscription.trial.description')}
              </p>
            </div>
            <StatusBadge status={translate('subscription.status.TRIALING')} variant="brand" size="sm" />
          </div>
          <FeatureList items={trialFeatures} theme={theme} />
          {canActivateTrial && (
            <Button variant="brand" fullWidth disabled={trialBusy || busyPlan !== null} onClick={handleStartTrial}>
              {trialBusy ? translate('common.loading') : translate('subscription.trial.activate')}
            </Button>
          )}
        </Card>
      )}

      {currentFeatures.length > 0 && (
        <Card noHover className="mb-6 !rounded-3xl !px-5 !py-2">
          <p
            className="text-xs font-bold uppercase tracking-widest pt-4 pb-1"
            style={{ color: theme.text.muted }}
          >
            {translate('subscription.access.title')}
          </p>
          {currentFeatures.map((feature, index) => (
            <div key={feature.label}>
              {index > 0 && <div style={{ borderTop: `1px solid ${theme.cardBorder}` }} />}
              <AccessRow
                label={feature.label}
                included
                hint={feature.hint}
                theme={theme}
              />
            </div>
          ))}
        </Card>
      )}

      {(upgradePlans.length > 0 || showSoloRestriction || showProRestriction) && currentRank < planRank('PRO') && (
        <section className="mb-8">
          <h3
            className="text-xs font-bold uppercase tracking-widest mb-3"
            style={{ color: theme.text.muted }}
          >
            {translate('subscription.choosePlan')}
          </h3>
          <div className="space-y-3">
            {upgradePlans.map((p) => {
              const features = p.code === 'SOLO' ? soloUnlocks : p.code === 'PRO' ? proUnlocks : [];

              return (
                <Card
                  key={p.code}
                  noHover
                  className="!rounded-3xl !p-5"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h4 className="text-lg font-bold mb-1" style={{ color: theme.text.primary }}>
                        {translate(`subscription.plans.${p.code}.name`)}
                      </h4>
                      <p className="text-sm leading-snug" style={{ color: theme.text.muted }}>
                        {translate(`subscription.plans.${p.code}.description`)}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-base font-bold tabular-nums" style={{ color: theme.text.primary }}>
                        {formatPrice(p.priceMonthly, p.currency)}
                      </p>
                      <p className="text-xs" style={{ color: theme.text.muted }}>
                        {translate('subscription.price.perMonth')}
                      </p>
                    </div>
                  </div>

                  <FeatureList items={features} theme={theme} />

                  <Button
                    variant="brand"
                    fullWidth
                    disabled={busyPlan !== null}
                    onClick={() => handleUpgrade(p.code)}
                  >
                    {busyPlan === p.code
                      ? translate('common.loading')
                      : `${translate('subscription.upgrade')} · ${formatPrice(p.priceMonthly, p.currency)}`}
                  </Button>
                </Card>
              );
            })}

            {showSoloRestriction && (
              <Card noHover className="!rounded-3xl !p-5 opacity-80">
                <h4 className="text-lg font-bold mb-1" style={{ color: theme.text.primary }}>
                  {translate('subscription.plans.SOLO.name')}
                </h4>
                <p className="text-sm leading-snug" style={{ color: theme.text.secondary }}>
                  {isCoachedTrainee
                    ? translate('subscription.eligibility.soloHiddenWithCoach')
                    : translate('subscription.eligibility.soloHiddenAsCoach')}
                </p>
                <FeatureList items={soloUnlocks} theme={theme} className="mt-3" />
              </Card>
            )}

            {showProRestriction && (
              <Card noHover className="!rounded-3xl !p-5 opacity-80">
                <h4 className="text-lg font-bold mb-1" style={{ color: theme.text.primary }}>
                  {translate('subscription.plans.PRO.name')}
                </h4>
                <p className="text-sm leading-snug" style={{ color: theme.text.secondary }}>
                  {translate('subscription.eligibility.proHiddenNotCoach')}
                </p>
                <FeatureList items={proUnlocks} theme={theme} className="mt-3" />
              </Card>
            )}
          </div>
          {upgradePlans.length > 0 && (
            <p className="text-xs text-center mt-3" style={{ color: theme.text.muted }}>
              {translate('subscription.paidWithStars')}
            </p>
          )}
        </section>
      )}

      {isPaid && !cancelAtPeriodEnd && (
        <Button variant="danger" fullWidth onClick={handleCancel}>
          {translate('subscription.cancelSubscription')}
        </Button>
      )}

      {isPaid && cancelAtPeriodEnd && periodLabel && (
        <p className="text-center text-sm" style={{ color: theme.text.muted }}>
          {periodLabel}
        </p>
      )}
    </div>
  );
}
